import InputT from '@components/tailwind-components/Input/InputT';
import SelectInputT from '@components/tailwind-components/Input/SelectInputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useDataSourcesInit } from '@hooks/dataSources/useDataSourcesInit';
import type { DataSourceSelectionModalProps } from '@interfaces/TDataSourceGeneration';
import type React from 'react';
import { useTranslation } from 'react-i18next';

export const DataSourceSelectionContent: React.FC<
  DataSourceSelectionModalProps
> = ({
  dataSourceSelection,
  setDataSourceSelection,
  hasRegularItems,
  hasPriorityItems,
}) => {
  const { t } = useTranslation();

  useDataSourcesInit(true);

  const {
    validSelectableFAQDataSources: dataSources,
    validSelectablePriorityDataSources: priorityDataSources,
  } = useDataSources();

  const isUpdateMode = dataSourceSelection.useExistingDataSourceFile;
  // In update mode the priority target is a dropdown when a priority file already exists;
  // with none yet it falls back to naming a new file.
  const hasExistingPriorityFiles = priorityDataSources.length > 0;

  const renderPriorityLabel = (dimmed: boolean) => (
    <LabelT
      text={t(
        'train-ai.data-source-generation.data-source-selection.priority.title',
      )}
      className={`text-tiny font-normal ${dimmed ? 'opacity-30' : '!text-amber-600'}`}
    />
  );

  const renderPrioritySaveNote = (textKey: string, dimmed: boolean) => (
    <div
      className={`text-pico font-normal text-gray-400 ${dimmed ? 'opacity-30' : ''}`}>
      {t(textKey)}
    </div>
  );

  return (
    <div className='h-full flex flex-col rounded-lg bg-gray-100 overflow-hidden border border-primary-dark'>
      <div className='flex bg-primary-dark text-white text-base font-normal py-1 px-3'>
        {t('train-ai.data-source-generation.data-source-selection')}
      </div>

      <div className='flex flex-col p-3 h-full overflow-y-auto'>
        <div className='flex text-xs font-normal text-primary-base mb-4'>
          {t('train-ai.data-source-generation.data-source-selection.desc')}
        </div>

        <div className='flex flex-col gap-5 flex-1 justify-between'>
          {/* Mode: update existing - standard + priority dropdowns */}
          <div className='flex flex-col'>
            <div className='flex gap-2 mb-3'>
              <InputT
                id='existing-data-source'
                type='radio'
                parentClassName='!mb-0'
                checked={isUpdateMode}
                options={{
                  onChange: () =>
                    setDataSourceSelection({
                      useExistingDataSourceFile: true,
                      existingFile: undefined,
                      newFileName: '',
                      priorityExistingFile: undefined,
                      priorityNewFileName: '',
                      error: '',
                      priorityError: '',
                    }),
                }}
              />
              <LabelT
                htmlFor='existing-data-source'
                text={t(
                  'train-ai.data-source-generation.data-source-selection.option1',
                )}
                className='text-sm font-medium text-primary-base cursor-pointer'
              />
            </div>

            <div className='flex flex-col gap-1 ml-[20px]'>
              {/* Standard target */}
              <LabelT
                text={t(
                  'train-ai.data-source-generation.data-source-selection.standard.label',
                )}
                className={`text-tiny font-normal ${!hasRegularItems ? 'opacity-30' : ''}`}
              />
              <SelectInputT
                parentClassName='w-full'
                className={`text-xs h-[30px] ${isUpdateMode && hasRegularItems && dataSourceSelection.error ? 'ring-red-base' : ''}`}
                values={dataSources.map((source) => ({
                  description: source.name,
                  value: source.key,
                }))}
                noOptionSelectedText={t(
                  'train-ai.data-source-generation.data-source-selection.option1.placeholder',
                )}
                value={dataSourceSelection?.existingFile?.key || ''}
                onChange={(event) => {
                  const existingFile = dataSources.find(
                    (source) => source.key === event.target.value,
                  );
                  setDataSourceSelection({
                    ...dataSourceSelection,
                    existingFile,
                    error: '',
                  });
                }}
                options={{ disabled: !isUpdateMode || !hasRegularItems }}
              />
              {!hasRegularItems && (
                <div className='text-gray-400 text-pico font-normal'>
                  {t(
                    'train-ai.data-source-generation.data-source-selection.only-priority-note',
                  )}
                </div>
              )}
              {isUpdateMode && hasRegularItems && dataSourceSelection.error && (
                <div className='text-red-base text-pico font-normal'>
                  {dataSourceSelection.error}
                </div>
              )}

              {/* Priority target */}
              {hasPriorityItems && (
                <>
                  {renderPriorityLabel(!isUpdateMode)}
                  {hasExistingPriorityFiles ? (
                    <SelectInputT
                      parentClassName='w-full'
                      className={`text-xs h-[30px] ${isUpdateMode && dataSourceSelection.priorityError ? 'ring-red-base' : ''}`}
                      values={priorityDataSources.map((source) => ({
                        description: source.name,
                        value: source.key,
                      }))}
                      noOptionSelectedText={t(
                        'train-ai.data-source-generation.data-source-selection.priority.existing.placeholder',
                      )}
                      value={
                        dataSourceSelection?.priorityExistingFile?.key || ''
                      }
                      onChange={(event) => {
                        const priorityExistingFile = priorityDataSources.find(
                          (source) => source.key === event.target.value,
                        );
                        setDataSourceSelection({
                          ...dataSourceSelection,
                          priorityExistingFile,
                          priorityError: '',
                        });
                      }}
                      options={{ disabled: !isUpdateMode }}
                    />
                  ) : (
                    <InputT
                      type='text'
                      parentClassName='!mb-0'
                      className={`text-xs h-[30px] ${isUpdateMode && dataSourceSelection.priorityError ? 'ring-red-base' : ''}`}
                      placeholder={t(
                        'train-ai.data-source-generation.data-source-selection.priority.new.placeholder',
                      )}
                      options={{
                        // Only reflect the shared value while this (update) mode is active,
                        // so the create-mode input below doesn't mirror it.
                        value: isUpdateMode
                          ? dataSourceSelection.priorityNewFileName || ''
                          : '',
                        onChange: (
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setDataSourceSelection({
                            ...dataSourceSelection,
                            priorityNewFileName: event.target.value,
                            priorityError: '',
                          });
                        },
                        disabled: !isUpdateMode,
                      }}
                    />
                  )}
                  {renderPrioritySaveNote(
                    hasExistingPriorityFiles
                      ? 'train-ai.data-source-generation.data-source-selection.priority.existing.note'
                      : 'train-ai.data-source-generation.data-source-selection.priority.no-file-note',
                    !isUpdateMode,
                  )}
                  {isUpdateMode && dataSourceSelection.priorityError && (
                    <div className='text-red-base text-pico font-normal'>
                      {dataSourceSelection.priorityError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mode: create new - standard + priority name inputs */}
          <div className='flex flex-col'>
            <div className='flex gap-2 mb-3'>
              <InputT
                id='new-data-source-option'
                type='radio'
                parentClassName='!mb-0'
                checked={!isUpdateMode}
                options={{
                  onChange: () =>
                    setDataSourceSelection({
                      useExistingDataSourceFile: false,
                      existingFile: undefined,
                      newFileName: '',
                      priorityExistingFile: undefined,
                      priorityNewFileName: '',
                      error: '',
                      priorityError: '',
                    }),
                }}
              />
              <LabelT
                htmlFor='new-data-source-option'
                text={t(
                  'train-ai.data-source-generation.data-source-selection.option2',
                )}
                className='text-sm font-medium text-primary-base cursor-pointer'
              />
            </div>

            <div className='flex flex-col gap-1 ml-[20px]'>
              {/* Standard target */}
              <LabelT
                text={t(
                  'train-ai.data-source-generation.data-source-selection.new-file.label',
                )}
                className={`text-tiny font-normal ${isUpdateMode || !hasRegularItems ? 'opacity-30' : ''}`}
              />
              <InputT
                type='text'
                parentClassName='!mb-0'
                className={`text-xs h-[30px] ${!isUpdateMode && hasRegularItems && dataSourceSelection.error ? 'ring-red-base' : ''}`}
                placeholder={t(
                  'train-ai.data-source-generation.data-source-selection.new-file.placeholder',
                )}
                options={{
                  value: dataSourceSelection.newFileName || '',
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    setDataSourceSelection({
                      ...dataSourceSelection,
                      newFileName: event.target.value,
                      error: '',
                    });
                  },
                  disabled: isUpdateMode || !hasRegularItems,
                }}
              />
              {!isUpdateMode &&
                hasRegularItems &&
                dataSourceSelection.error && (
                  <div className='text-red-base text-pico font-normal'>
                    {dataSourceSelection.error}
                  </div>
                )}

              {/* Priority target */}
              {hasPriorityItems && (
                <>
                  {renderPriorityLabel(isUpdateMode)}
                  <InputT
                    type='text'
                    parentClassName='!mb-0'
                    className={`text-xs h-[30px] ${!isUpdateMode && dataSourceSelection.priorityError ? 'ring-red-base' : ''}`}
                    placeholder={t(
                      'train-ai.data-source-generation.data-source-selection.priority.new.placeholder',
                    )}
                    options={{
                      // Only reflect the shared value while this (create) mode is active,
                      // so it doesn't mirror what was typed in the update-mode input above.
                      value: !isUpdateMode
                        ? dataSourceSelection.priorityNewFileName || ''
                        : '',
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setDataSourceSelection({
                          ...dataSourceSelection,
                          priorityNewFileName: event.target.value,
                          priorityError: '',
                        });
                      },
                      disabled: isUpdateMode,
                    }}
                  />
                  {renderPrioritySaveNote(
                    'train-ai.data-source-generation.data-source-selection.priority.save-note',
                    isUpdateMode,
                  )}
                  {!isUpdateMode && dataSourceSelection.priorityError && (
                    <div className='text-red-base text-pico font-normal'>
                      {dataSourceSelection.priorityError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataSourceSelectionContent;
